// ZohoSmsHelpModal.js
import React, { useState } from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
} from "mdb-react-ui-kit";

function ZohoSmsHelpModal({ triggerVariant = "link" }) {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen(!open);

  const renderTrigger = () => {
    if (triggerVariant === "button") {
      return (
        <MDBBtn color="info" size="sm" onClick={toggleOpen}>
          Zoho SMS Setup Help
        </MDBBtn>
      );
    }

    // Default: link-style trigger
    return (
      <span
        onClick={toggleOpen}
        style={{
          cursor: "pointer",
          color: "#0d6efd",
          textDecoration: "underline",
          fontSize: "0.8rem",
          marginLeft: "8px",
        }}
      >
        How to configure Zoho for SMS logging
      </span>
    );
  };

  return (
    <>
      {renderTrigger()}

      <MDBModal open={open} setOpen={setOpen} tabIndex="-1">
        <MDBModalDialog size="lg" scrollable>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Zoho SMS Logging – Setup Guide</MDBModalTitle>
              <MDBBtn
                className="btn-close"
                color="none"
                aria-label="Close"
                onClick={toggleOpen}
              ></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <p style={{ fontWeight: 600, marginBottom: 8 }}>
                What this setup is for
              </p>
              <p>
                Streams.AI logs SMS into the <strong>Calls</strong> module in Zoho
                as an activity related to the Lead/Contact. The custom fields
                below are <strong>optional</strong> – they are recommended to
                make it easier for you to <strong>view, filter and report</strong>
                on SMS activities inside Zoho.
              </p>
              <p>
                If you do <strong>not</strong> create these fields, SMS events
                will still be logged using the standard call fields (such as{" "}
                <code className="spical_text">Subject</code> and <code className="spical_text">Description</code>), but you will
                have <strong>limited ability</strong> to:
              </p>
              <ul>
                <li>Filter calls specifically by &quot;SMS&quot; type</li>
                <li>Build reports/dashboards based on SMS-only activities</li>
              </ul>

              <hr />

              <p style={{ fontWeight: 600, marginBottom: 8 }}>
                Optional fields to create in the Zoho &quot;Calls&quot; module
              </p>
              <ol>
                <li>
                  <strong>SMS Activity Type</strong> (Picklist)
                  <ul>
                    <li>Module: Calls</li>
                    <li>Field type: Picklist</li>
                    <li>API name: <code className="spical_text">SMS_Activity_Type</code></li>
                    <li>Include value: <code className="spical_text">SMS</code></li>
                    <li>
                      Used by Streams.AI (when present) to classify the activity as
                      an SMS.
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>Call Subtype</strong> (Picklist)
                  <ul>
                    <li>Module: Calls</li>
                    <li>Field type: Picklist</li>
                    <li>API name: <code className="spical_text">Call_Subtype</code></li>
                    <li>Include value: <code className="spical_text">SMS</code></li>
                    <li>
                      Optional extra classification for SMS vs other call types.
                    </li>
                  </ul>
                </li>
                
              </ol>

              <p style={{ fontSize: "1rem", marginTop: 4 }}>
                If these fields are not configured, Streams.AI simply skips
                populating them and continues logging the SMS using the standard
                fields, so the feature still works with a reduced set of data.
              </p>

              <hr />

              <p style={{ fontWeight: 600, marginBottom: 8 }}>
                Steps in Zoho CRM
              </p>
              <ol>
                <li>
                  Go to{" "}
                  <strong>Setup → Customization → Modules and Fields</strong>.
                </li>
                <li>
                  Select the <strong>Calls</strong> module and your active
                  layout.
                </li>
                <li>
                  Add / verify the optional fields above with the exact API
                  names listed (if you want richer SMS classification).
                </li>
                <li>Save the layout and make sure it’s active for your users.</li>
              </ol>

              <p style={{ fontStyle: "italic", fontSize: "1rem" }}>
               <b> Note:</b> If your Zoho admin uses different API names, please
                contact Streams.AI support so we can adjust the integration
                mapping. The SMS logging feature itself does not depend on these
                custom fields; they only enhance how records appear and can be
                filtered in Zoho.
              </p>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" size="sm" onClick={toggleOpen}>
                Close
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </>
  );
}

export default ZohoSmsHelpModal;
